import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Link } from "react-router-dom";
import { Briefcase, Wrench, MapPin } from "lucide-react";

// Fix for default marker icons in React-Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// Custom icons for jobs and services
const createIcon = (color, emoji) => {
  return L.divIcon({
    className: "custom-marker",
    html: `<div style="
      background: ${color};
      width: 36px;
      height: 36px;
      border-radius: 50% 50% 50% 0;
      transform: rotate(-45deg);
      display: flex;
      align-items: center;
      justify-content: center;
      border: 3px solid white;
      box-shadow: 0 3px 10px rgba(0,0,0,0.3);
    ">
      <span style="transform: rotate(45deg); font-size: 16px;">${emoji}</span>
    </div>`,
    iconSize: [36, 36],
    iconAnchor: [18, 36],
    popupAnchor: [0, -36],
  });
};

const jobIcon = createIcon("#004E64", "💼");
const serviceIcon = createIcon("#10B981", "🔧");

// Coordinates of main Ticino cities/towns
const TICINO_LOCATIONS = {
  // Distretto di Lugano
  "Lugano": [46.0037, 8.9511],
  "Paradiso": [45.9872, 8.9456],
  "Massagno": [46.0128, 8.9367],
  "Viganello": [46.0156, 8.9622],
  "Pregassona": [46.0233, 8.9678],
  "Breganzona": [46.0011, 8.9222],
  "Savosa": [46.0183, 8.9244],
  "Sorengo": [45.9933, 8.9300],
  "Muzzano": [45.9856, 8.9122],
  "Agno": [45.9933, 8.9011],
  "Bioggio": [46.0078, 8.9033],
  "Manno": [46.0322, 8.9189],
  "Cadempino": [46.0411, 8.9333],
  "Lamone": [46.0489, 8.9378],
  "Caslano": [45.9633, 8.8756],
  "Magliaso": [45.9800, 8.8844],
  "Collina d'Oro": [45.9567, 8.9122],
  "Morcote": [45.9322, 8.9222],
  "Melide": [45.9511, 8.9456],
  "Bissone": [45.9567, 8.9611],
  "Campione d'Italia": [45.9689, 8.9744],
  "Capriasca": [46.0678, 8.9844],
  "Monteceneri": [46.0944, 8.9233],
  "Torricella-Taverne": [46.0567, 8.9044],
  
  // Distretto di Bellinzona
  "Bellinzona": [46.1956, 9.0244],
  "Giubiasco": [46.1722, 9.0089],
  "Camorino": [46.1522, 9.0178],
  "Sant'Antonino": [46.1533, 9.0000],
  "Cadenazzo": [46.1478, 8.9522],
  "Arbedo-Castione": [46.2111, 9.0433],
  "Lumino": [46.2289, 9.0578],
  "Sementina": [46.1844, 9.0011],
  
  // Distretto di Locarno
  "Locarno": [46.1711, 8.7956],
  "Ascona": [46.1567, 8.7733],
  "Minusio": [46.1778, 8.8111],
  "Muralto": [46.1722, 8.8044],
  "Tenero-Contra": [46.1744, 8.8489],
  "Gordola": [46.1822, 8.8600],
  "Losone": [46.1633, 8.7533],
  "Brissago": [46.1233, 8.7100],
  "Gambarogno": [46.1289, 8.8711],
  "Orselina": [46.1756, 8.7889],
  
  // Distretto di Mendrisio
  "Mendrisio": [45.8711, 8.9811],
  "Chiasso": [45.8333, 9.0300],
  "Balerna": [45.8478, 9.0089],
  "Stabio": [45.8533, 8.9378],
  "Riva San Vitale": [45.9033, 8.9644],
  "Coldrerio": [45.8544, 9.0011],
  "Vacallo": [45.8456, 9.0211],
  "Morbio Inferiore": [45.8544, 9.0144],
  "Novazzano": [45.8411, 8.9789],
  "Castel San Pietro": [45.8644, 9.0167],
  
  // Distretto di Leventina
  "Airolo": [46.5289, 8.6122],
  "Faido": [46.4778, 8.8011],
  "Biasca": [46.3578, 8.9711],
  "Bodio": [46.3800, 8.9122],
  "Giornico": [46.4011, 8.8711],
  "Quinto": [46.5122, 8.7067],
  
  // Distretto di Blenio
  "Acquarossa": [46.4567, 8.9378],
  "Olivone": [46.5311, 8.9411],
  
  // Distretto di Vallemaggia
  "Maggia": [46.2478, 8.7067],
  "Cevio": [46.3111, 8.6022],
  "Avegno Gordevio": [46.2022, 8.7456],
};

// Center of Ticino
const TICINO_CENTER = [46.15, 8.95];
const TICINO_BOUNDS = [[45.8, 8.4], [46.6, 9.2]];

function MapController({ selectedLocation }) {
  const map = useMap();
  
  useEffect(() => {
    if (selectedLocation && TICINO_LOCATIONS[selectedLocation]) {
      map.flyTo(TICINO_LOCATIONS[selectedLocation], 13, { duration: 1 });
    }
  }, [selectedLocation, map]);
  
  return null;
}

export function TicinoMap({ jobs = [], services = [], onLocationSelect, height = "400px" }) {
  const [selectedLocation, setSelectedLocation] = useState(null);

  // Group items by location
  const jobsByLocation = {};
  const servicesByLocation = {};

  jobs.forEach(job => {
    const loc = job.location || "Lugano";
    if (!jobsByLocation[loc]) jobsByLocation[loc] = [];
    jobsByLocation[loc].push(job);
  });

  services.forEach(service => {
    const loc = service.location || "Lugano";
    if (!servicesByLocation[loc]) servicesByLocation[loc] = [];
    servicesByLocation[loc].push(service);
  });

  const handleMarkerClick = (location) => {
    setSelectedLocation(location);
    if (onLocationSelect) onLocationSelect(location);
  };

  return (
    <div className="relative rounded-xl overflow-hidden shadow-lg border border-slate-200" style={{ height }}>
      <MapContainer
        center={TICINO_CENTER}
        zoom={10}
        minZoom={9}
        maxZoom={15}
        maxBounds={TICINO_BOUNDS}
        maxBoundsViscosity={1.0}
        style={{ height: "100%", width: "100%" }}
        className="z-0"
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        
        <MapController selectedLocation={selectedLocation} />
        
        {/* Job Markers */}
        {Object.entries(jobsByLocation).map(([location, locationJobs]) => {
          const coords = TICINO_LOCATIONS[location];
          if (!coords) return null;
          
          return (
            <Marker 
              key={`job-${location}`}
              position={coords}
              icon={jobIcon}
              eventHandlers={{
                click: () => handleMarkerClick(location)
              }}
            >
              <Popup>
                <div className="p-2 min-w-[200px]">
                  <div className="flex items-center gap-2 mb-2 pb-2 border-b">
                    <div className="w-8 h-8 bg-[#004E64] rounded-full flex items-center justify-center">
                      <Briefcase className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900">{location}</h3>
                      <p className="text-xs text-slate-500">{locationJobs.length} annunci lavoro</p>
                    </div>
                  </div>
                  <div className="space-y-2 max-h-[150px] overflow-y-auto">
                    {locationJobs.slice(0, 3).map((job, i) => (
                      <Link 
                        key={i}
                        to={`/jobs/${job.id}`}
                        className="block p-2 bg-slate-50 rounded hover:bg-slate-100 transition-colors"
                      >
                        <p className="font-medium text-sm text-slate-800 truncate">{job.title}</p>
                        <p className="text-xs text-slate-500">{job.company_name}</p>
                      </Link>
                    ))}
                    {locationJobs.length > 3 && (
                      <Link 
                        to={`/jobs?location=${location}`}
                        className="block text-center text-sm text-[#004E64] font-medium hover:underline"
                      >
                        Vedi tutti ({locationJobs.length})
                      </Link>
                    )}
                  </div>
                </div>
              </Popup>
            </Marker>
          );
        })}
        
        {/* Service Markers - offset slightly if same location as job */}
        {Object.entries(servicesByLocation).map(([location, locationServices]) => {
          let coords = TICINO_LOCATIONS[location];
          if (!coords) return null;
          
          // Offset if there's also a job marker here
          if (jobsByLocation[location]) {
            coords = [coords[0] + 0.003, coords[1] + 0.003];
          }
          
          return (
            <Marker 
              key={`service-${location}`}
              position={coords}
              icon={serviceIcon}
              eventHandlers={{
                click: () => handleMarkerClick(location)
              }}
            >
              <Popup>
                <div className="p-2 min-w-[200px]">
                  <div className="flex items-center gap-2 mb-2 pb-2 border-b">
                    <div className="w-8 h-8 bg-emerald-500 rounded-full flex items-center justify-center">
                      <Wrench className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900">{location}</h3>
                      <p className="text-xs text-slate-500">{locationServices.length} servizi</p>
                    </div>
                  </div>
                  <div className="space-y-2 max-h-[150px] overflow-y-auto">
                    {locationServices.slice(0, 3).map((service, i) => (
                      <Link 
                        key={i}
                        to={`/services/${service.id}`}
                        className="block p-2 bg-slate-50 rounded hover:bg-slate-100 transition-colors"
                      >
                        <p className="font-medium text-sm text-slate-800 truncate">{service.title}</p>
                        <p className="text-xs text-slate-500">{service.rate}</p>
                      </Link>
                    ))}
                    {locationServices.length > 3 && (
                      <Link 
                        to={`/services?location=${location}`}
                        className="block text-center text-sm text-emerald-600 font-medium hover:underline"
                      >
                        Vedi tutti ({locationServices.length})
                      </Link>
                    )}
                  </div>
                </div>
              </Popup>
            </Marker>
          );
        })}
      </MapContainer>
      
      {/* Legend */}
      <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur-sm rounded-lg shadow-lg p-3 z-[1000]">
        <p className="text-xs font-semibold text-slate-700 mb-2">Legenda</p>
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 bg-[#004E64] rounded-full flex items-center justify-center">
              <span className="text-[10px]">💼</span>
            </div>
            <span className="text-xs text-slate-600">Annunci lavoro</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-5 h-5 bg-emerald-500 rounded-full flex items-center justify-center">
              <span className="text-[10px]">🔧</span>
            </div>
            <span className="text-xs text-slate-600">Servizi</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TicinoMap;
